import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { fetchFactoryDetailForManagement, updateFactory, deleteFactory } from '../../services/api';
import './FactoryDetailPage.css';

function FactoryDetailPage() {
    const navigate = useNavigate();
    const { factoryId } = useParams();
    const [searchParams] = useSearchParams();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [originalData, setOriginalData] = useState(null);

    // URL 쿼리 파라미터에서 edit 모드 확인
    useEffect(() => {
        const editMode = searchParams.get('edit');
        if (editMode === 'true') {
            setIsEditing(true);
        } else {
            setIsEditing(false);
        }
    }, [searchParams]);

    // API에서 공장 데이터 가져오기
    const [formData, setFormData] = useState({
        id: factoryId,
        name: '',
        code: '',
        location: '',
        address: '',
        manager: '',
        contact: '',
        email: '',
        totalEquipment: 0,
        runningEquipment: 0,
        idleEquipment: 0,
        stoppedEquipment: 0,
        temperature: '-',
        humidity: '-',
        operationRate: 0,
        status: '정상',
        lastInspection: '-',
        establishedDate: '-',
        description: ''
    });

    useEffect(() => {
        const loadFactoryDetail = async () => {
            try {
                setLoading(true);
                const data = await fetchFactoryDetailForManagement(factoryId);
                

                // API 데이터를 formData 형식으로 변환
                const transformedData = {
                    id: data.id,
                    name: data.factoryName || '',
                    code: data.factoryCode || '',
                    location: data.address || '',
                    address: data.address || '',
                    manager: data.managerName || '',
                    contact: data.managerPhone || '',
                    email: data.managerEmail || '',
                    totalEquipment: data.totalEquipment || 0,
                    runningEquipment: data.runningEquipment || 0,
                    idleEquipment: data.idleEquipment || 0,
                    stoppedEquipment: data.stoppedEquipment || 0,
                    temperature: data.temperature ? `${data.temperature}°C` : '-',
                    humidity: data.humidity ? `${data.humidity}%` : '-',
                    operationRate: data.operationRate || 0,
                    status: data.status === 'NORMAL' ? '정상' : data.status === 'WARNING' ? '경고' : '이상',
                    lastInspection: data.lastInspectionDate || '-',
                    establishedDate: data.createdAt ? data.createdAt.split('T')[0] : '-',
                    description: data.description || ''
                };

                setFormData(transformedData);
                setOriginalData(transformedData);
            } catch (err) {
                console.error('공장 상세정보 조회 실패:', err);
                alert('공장 정보를 불러오는데 실패했습니다.');
            } finally {
                setLoading(false);
            }
        };

        if (factoryId) {
            loadFactoryDetail();
        }
    }, [factoryId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEdit = () => {
        // URL에 ?edit=true 쿼리 파라미터 추가
        navigate(`/factory/${factoryId}?edit=true`, { replace: true });
    };

    const handleCancel = () => {
        if (isEditing) {
            // URL에서 ?edit=true 제거
            navigate(`/factory/${factoryId}`, { replace: true });
            // 원래 데이터로 복원
            if (originalData) {
                setFormData(originalData);
            }
        } else {
            navigate('/factory-list');
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();

        try {
            // API 형식에 맞게 데이터 변환
            const requestData = {
                factoryCode: formData.code,
                factoryName: formData.name,
                address: formData.address,
                latitude: null,
                longitude: null,
                managerName: formData.manager,
                managerPhone: formData.contact,
                managerEmail: formData.email,
                status: formData.status === '정상' ? 'NORMAL' : formData.status === '경고' ? 'WARNING' : 'ERROR',
                description: formData.description,
                lastInspectionDate: formData.lastInspection !== '-' ? formData.lastInspection : null
            };

            
            await updateFactory(factoryId, requestData);

            alert('공장 정보가 성공적으로 수정되었습니다.');
            // 수정 모드 해제 및 데이터 다시 로드
            navigate(`/factory/${factoryId}`, { replace: true });

            // 페이지 새로고침하여 최신 데이터 로드
            window.location.reload();
        } catch (err) {
            console.error('공장 정보 수정 실패:', err);
            alert('공장 정보 수정에 실패했습니다.');
        }
    };

    const handleDelete = async () => {
        if (window.confirm('정말로 이 공장을 삭제하시겠습니까?\n삭제된 데이터는 복구할 수 없습니다.')) {
            try {
                
                await deleteFactory(factoryId);

                alert('공장이 성공적으로 삭제되었습니다.');
                navigate('/factory-list');
            } catch (err) {
                console.error('공장 삭제 실패:', err);
                alert('공장 삭제에 실패했습니다.');
            }
        }
    };

    const getStatusClass = (status) => {
        switch (status) {
            case '정상':
                return 'factory-status-normal';
            case '경고':
                return 'factory-status-warning';
            case '이상':
                return 'factory-status-error';
            default:
                return 'factory-status-normal';
        }
    };

    const getOperationRateClass = (rate) => {
        if (rate >= 85) return 'factory-rate-high';
        if (rate >= 70) return 'factory-rate-medium';
        return 'factory-rate-low';
    };

    return (
        <MainLayout>
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'white' }}>
                    데이터를 불러오는 중...
                </div>
            ) : (
            <div className="factory-detail-container">
                {/* Header Section */}
                <div className="factory-detail-header">
                    <div className="factory-detail-header-left">
                        <div className="factory-back-btn" onClick={() => navigate('/factory-list')}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div>
                            <div className="factory-detail-title">공장 상세 정보</div>
                            <p className="factory-detail-subtitle">공장 정보 조회 및 수정</p>
                        </div>
                    </div>
                    <div className="factory-detail-header-right">
                        {!isEditing ? (
                            <>
                                <button className="factory-delete-header-btn" onClick={handleDelete}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M2 4H3.33333H14M5.33333 4V2.66667C5.33333 2.31305 5.47381 1.97391 5.72386 1.72386C5.97391 1.47381 6.31305 1.33333 6.66667 1.33333H9.33333C9.68696 1.33333 10.0261 1.47381 10.2761 1.72386C10.5262 1.97391 10.6667 2.31305 10.6667 2.66667V4M12.6667 4V13.3333C12.6667 13.687 12.5262 14.0261 12.2761 14.2761C12.0261 14.5262 11.687 14.6667 11.3333 14.6667H4.66667C4.31305 14.6667 3.97391 14.5262 3.72386 14.2761C3.47381 14.0261 3.33333 13.687 3.33333 13.3333V4H12.6667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    삭제
                                </button>
                                <button className="factory-edit-header-btn" onClick={handleEdit}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M11.3333 2C11.5084 1.82493 11.7163 1.68605 11.9457 1.59135C12.1752 1.49665 12.4218 1.44803 12.6707 1.44803C12.9196 1.44803 13.1662 1.49665 13.3956 1.59135C13.6251 1.68605 13.833 1.82493 14.008 2C14.183 2.17507 14.3219 2.38299 14.4166 2.61245C14.5113 2.84191 14.5599 3.08852 14.5599 3.33744C14.5599 3.58636 14.5113 3.83297 14.4166 4.06243C14.3219 4.29189 14.183 4.49981 14.008 4.67488L5.33333 13.3495L1.33333 14.683L2.66667 10.6829L11.3333 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    수정
                                </button>
                            </>
                        ) : (
                            <>
                                <button className="factory-cancel-header-btn" onClick={handleCancel}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    취소
                                </button>
                                <button className="factory-save-header-btn" onClick={handleSave}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    저장
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Content Section */}
                <div className="factory-detail-content">
                    {/* Factory Profile Card */}
                    <div className="factory-profile-card">
                        <div className="factory-profile-icon-large">
                            <span className="material-symbols-outlined">factory</span>
                        </div>
                        <div className="factory-profile-info">
                            <h2 className="factory-profile-name">{formData.name}</h2>
                            <p className="factory-profile-code">{formData.code}</p>
                            <span className={`factory-status-badge-large ${getStatusClass(formData.status)}`}>
                                {formData.status}
                            </span>
                        </div>
                        <div className="factory-profile-stats">
                            <div className="factory-stat-item">
                                <span className="factory-stat-label">총 설비</span>
                                <span className="factory-stat-value">{formData.totalEquipment}대</span>
                            </div>
                            <div className="factory-stat-item">
                                <span className="factory-stat-label">가동률</span>
                                <div className="factory-stat-rate">
                                    <div className="factory-rate-bar-container">
                                        <div
                                            className={`factory-rate-bar ${getOperationRateClass(formData.operationRate)}`}
                                            style={{ width: `${formData.operationRate}%` }}
                                        ></div>
                                    </div>
                                    <span className="factory-stat-value">{formData.operationRate}%</span>
                                </div>
                            </div>
                        </div>
                        <div className="factory-profile-meta">
                            <div className="factory-meta-item">
                                <span className="factory-meta-label">설립일</span>
                                <span className="factory-meta-value">{formData.establishedDate}</span>
                            </div>
                            <div className="factory-meta-item">
                                <span className="factory-meta-label">최종점검일</span>
                                <span className="factory-meta-value">{formData.lastInspection}</span>
                            </div>
                        </div>
                    </div>

                    {/* Detail Form */}
                    <form onSubmit={handleSave}>
                        {/* 기본 정보 섹션 */}
                        <div className="factory-detail-section">
                            <h3 className="factory-detail-section-title">기본 정보</h3>
                            <div className="factory-detail-grid">
                                <div className="factory-detail-group">
                                    <label className="factory-detail-label">공장명</label>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            name="name"
                                            className="factory-detail-input"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    ) : (
                                        <div className="factory-detail-value">{formData.name}</div>
                                    )}
                                </div>

                                <div className="factory-detail-group">
                                    <label className="factory-detail-label">공장 코드</label>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            name="code"
                                            className="factory-detail-input"
                                            value={formData.code}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    ) : (
                                        <div className="factory-detail-value">{formData.code}</div>
                                    )}
                                </div>

                                <div className="factory-detail-group">
                                    <label className="factory-detail-label">위치</label>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            name="location"
                                            className="factory-detail-input"
                                            value={formData.location}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    ) : (
                                        <div className="factory-detail-value">{formData.location}</div>
                                    )}
                                </div>

                                <div className="factory-detail-group">
                                    <label className="factory-detail-label">상세 주소</label>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            name="address"
                                            className="factory-detail-input"
                                            value={formData.address}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    ) : (
                                        <div className="factory-detail-value">{formData.address}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 담당자 정보 섹션 */}
                        <div className="factory-detail-section">
                            <h3 className="factory-detail-section-title">담당자 정보</h3>
                            <div className="factory-detail-grid">
                                <div className="factory-detail-group">
                                    <label className="factory-detail-label">담당자명</label>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            name="manager"
                                            className="factory-detail-input"
                                            value={formData.manager}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    ) : (
                                        <div className="factory-detail-value">{formData.manager}</div>
                                    )}
                                </div>

                                <div className="factory-detail-group">
                                    <label className="factory-detail-label">연락처</label>
                                    {isEditing ? (
                                        <input
                                            type="tel"
                                            name="contact"
                                            className="factory-detail-input"
                                            value={formData.contact}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    ) : (
                                        <div className="factory-detail-value">{formData.contact}</div>
                                    )}
                                </div>

                                <div className="factory-detail-group">
                                    <label className="factory-detail-label">이메일</label>
                                    {isEditing ? (
                                        <input
                                            type="email"
                                            name="email"
                                            className="factory-detail-input"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    ) : (
                                        <div className="factory-detail-value">{formData.email}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 운영 정보 섹션 */}
                        <div className="factory-detail-section">
                            <h3 className="factory-detail-section-title">운영 정보</h3>
                            <div className="factory-detail-grid">
                                <div className="factory-detail-group">
                                    <label className="factory-detail-label">총 설비 수</label>
                                    {isEditing ? (
                                        <input
                                            type="number"
                                            name="totalEquipment"
                                            className="factory-detail-input"
                                            value={formData.totalEquipment}
                                            onChange={handleInputChange}
                                            required
                                            min="0"
                                        />
                                    ) : (
                                        <div className="factory-detail-value">{formData.totalEquipment}대</div>
                                    )}
                                </div>

                                <div className="factory-detail-group">
                                    <label className="factory-detail-label">가동 중</label>
                                    <div className="factory-detail-value factory-equipment-running">{formData.runningEquipment}대</div>
                                </div>

                                <div className="factory-detail-group">
                                    <label className="factory-detail-label">대기</label>
                                    <div className="factory-detail-value factory-equipment-idle">{formData.idleEquipment}대</div>
                                </div>

                                <div className="factory-detail-group">
                                    <label className="factory-detail-label">정지</label>
                                    <div className="factory-detail-value factory-equipment-stopped">{formData.stoppedEquipment}대</div>
                                </div>

                                <div className="factory-detail-group">
                                    <label className="factory-detail-label">상태</label>
                                    {isEditing ? (
                                        <div className="factory-detail-dropdown-container">
                                            <select
                                                name="status"
                                                className="factory-detail-select"
                                                value={formData.status}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="정상">정상</option>
                                                <option value="경고">경고</option>
                                                <option value="이상">이상</option>
                                            </select>
                                            <svg className="factory-detail-dropdown-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                    ) : (
                                        <div className="factory-detail-value">{formData.status}</div>
                                    )}
                                </div>

                                <div className="factory-detail-group factory-detail-group-full">
                                    <label className="factory-detail-label">비고</label>
                                    {isEditing ? (
                                        <textarea
                                            name="description"
                                            className="factory-detail-textarea"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            rows="4"
                                        />
                                    ) : (
                                        <div className="factory-detail-value">{formData.description}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 환경 정보 섹션 */}
                        <div className="factory-detail-section">
                            <h3 className="factory-detail-section-title">환경 정보</h3>
                            <div className="factory-detail-grid">
                                <div className="factory-detail-group">
                                    <label className="factory-detail-label">현재 온도</label>
                                    <div className="factory-detail-value">{formData.temperature}</div>
                                </div>

                                <div className="factory-detail-group">
                                    <label className="factory-detail-label">현재 습도</label>
                                    <div className="factory-detail-value">{formData.humidity}</div>
                                </div>
                            </div>
                        </div>

                        {/* Form Actions */}
                        {isEditing && (
                            <div className="factory-detail-form-actions">
                                <button type="button" className="factory-detail-btn-secondary" onClick={handleCancel}>
                                    취소
                                </button>
                                <button type="submit" className="factory-detail-btn-primary">
                                    저장
                                </button>
                            </div>
                        )}
                    </form>
                </div>
            </div>
            )}
        </MainLayout>
    );
}

export default FactoryDetailPage;
